import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../lib/api.js";
import {
  createBudgetCategory,
  deleteBudgetCategory,
  listBudgetCategories,
  updateBudgetCategory,
} from "../lib/api-modules/budget-categories.js";
import {
  type BudgetCategory,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { useAuth } from "../lib/auth-context.js";
import { centsToDollars, dollarsToCents } from "../lib/util/money.js";
import { ErrorMessage } from "./auth/error-message.js";
import { ConfirmButton } from "./confirm-button.js";

/**
 * <CategoriesPanel> — household-scoped budget category list with
 * inline create + edit + deactivate. Embeddable on `/spending`
 * (Phase 5 Session 2 Cleave 3) AND, in the future, on a dedicated
 * settings page.
 *
 * v1 supports a single level of nesting only — sub-categories must
 * point at a top-level category. The handler enforces this; the UI
 * keeps it simple by not surfacing the parent picker (categories are
 * flat in v1; the nested tree primitive is in the schema for
 * Phase 5.3+ to lean on if/when needed).
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function CategoriesPanel() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["budget-categories", { includeInactive: showInactive }],
    queryFn: () => listBudgetCategories(showInactive),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deleteBudgetCategory(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["budget-categories"] }),
  });
  const reactivateMutation = useMutation({
    mutationFn: (id: string) =>
      updateBudgetCategory(id, { is_active: true }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["budget-categories"] }),
  });

  if (isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading categories…
      </p>
    );
  }
  if (error) {
    return (
      <ErrorMessage>
        {error instanceof ApiError
          ? error.message
          : "Couldn't load categories."}
      </ErrorMessage>
    );
  }

  const categories = data?.budget_categories ?? [];
  const isMutating =
    deactivateMutation.isPending || reactivateMutation.isPending;

  return (
    <section
      aria-label="Budget categories"
      className="rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950"
    >
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Categories</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {canWrite && !showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="rounded-md bg-brand-500 px-2 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
            >
              Add category
            </button>
          )}
        </div>
      </header>

      {showCreate && (
        <CategoryForm
          mode="create"
          onClose={() => setShowCreate(false)}
        />
      )}

      {categories.length === 0 && !showCreate && (
        <p className="rounded-md border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500 dark:border-zinc-700">
          No categories yet.{" "}
          {canWrite ? "Click \"Add category\" to start." : ""}
        </p>
      )}

      <ul className="flex flex-col divide-y divide-zinc-200 dark:divide-zinc-800">
        {categories.map((c) =>
          editingId === c.category_id ? (
            <li key={c.category_id} className="py-2">
              <CategoryForm
                mode="edit"
                category={c}
                onClose={() => setEditingId(null)}
              />
            </li>
          ) : (
            <CategoryRow
              key={c.category_id}
              category={c}
              canWrite={canWrite}
              onEdit={() => setEditingId(c.category_id)}
              onDeactivate={() => deactivateMutation.mutate(c.category_id)}
              onReactivate={() => reactivateMutation.mutate(c.category_id)}
              isMutating={isMutating}
            />
          ),
        )}
      </ul>
    </section>
  );
}

function CategoryRow({
  category,
  canWrite,
  onEdit,
  onDeactivate,
  onReactivate,
  isMutating,
}: {
  category: BudgetCategory;
  canWrite: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  isMutating: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-2 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {category.name}
          {!category.is_active && (
            <span className="ml-2 rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              inactive
            </span>
          )}
          {category.is_essential && (
            <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-normal text-brand-800 dark:bg-brand-900/40 dark:text-brand-300">
              essential
            </span>
          )}
        </p>
        {category.monthly_budget_cents !== undefined && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Budget ${centsToDollars(category.monthly_budget_cents)} / month
          </p>
        )}
      </div>
      {canWrite && (
        <div className="flex shrink-0 items-center gap-1.5">
          {category.is_active ? (
            <>
              <button
                type="button"
                onClick={onEdit}
                disabled={isMutating}
                className="rounded-md border border-zinc-300 px-2 py-0.5 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Edit
              </button>
              <ConfirmButton
                label="Deactivate"
                pendingLabel="…"
                size="xs"
                ariaLabel={`Deactivate ${category.name}`}
                onConfirm={onDeactivate}
                isPending={isMutating}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={onReactivate}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-0.5 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Reactivate
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function CategoryForm({
  mode,
  category,
  onClose,
}: {
  mode: "create" | "edit";
  category?: BudgetCategory;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const [name, setName] = useState(category?.name ?? "");
  const [budgetDollars, setBudgetDollars] = useState<string>(
    category?.monthly_budget_cents !== undefined
      ? centsToDollars(category.monthly_budget_cents)
      : "",
  );
  const [isEssential, setIsEssential] = useState<boolean>(
    category?.is_essential ?? false,
  );
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const budgetCents = budgetDollars.trim().length > 0
        ? dollarsToCents(budgetDollars)
        : undefined;
      if (isEdit && category) {
        const input: Record<string, unknown> = {
          name: trimmedName,
          is_essential: isEssential,
        };
        if (budgetCents !== undefined) input["monthly_budget_cents"] = budgetCents;
        return updateBudgetCategory(category.category_id, input);
      }
      return createBudgetCategory({
        name: trimmedName,
        is_essential: isEssential,
        ...(budgetCents !== undefined
          ? { monthly_budget_cents: budgetCents }
          : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["budget-categories"],
      });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save category. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (name.trim().length === 0) {
      setInlineError("Name is required.");
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit category" : "Add category"}
      className="my-2 flex flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="grow">
          <label className="mb-1 block text-xs font-medium">Name</label>
          <input
            type="text"
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={mutation.isPending}
            placeholder="Groceries"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Monthly budget (USD)
          </label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            className={inputClass}
            value={budgetDollars}
            onChange={(e) => setBudgetDollars(e.target.value)}
            disabled={mutation.isPending}
            placeholder="800.00"
          />
        </div>
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={isEssential}
            onChange={(e) => setIsEssential(e.target.checked)}
            disabled={mutation.isPending}
          />
          Essential
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-2 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : isEdit ? "Save" : "Add"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";
